import { useCallback, useEffect, useState } from 'react'

import { generatePath, useNavigate } from 'react-router'
import { Paths } from 'src/constants/paths'
import { usePreSession } from 'src/features/explanation/hooks/usePreSession'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import { GqlError } from 'src/lib/gql-client'

import { useRoomMediaContext } from '../context/MediaProvider'

type UsePatientLobbyProps = {
  trialUid: string
  explanationRevisionUid: string
  explanationReservationUid: string | undefined
}

export const usePatientLobby = (props: UsePatientLobbyProps) => {
  const { trialUid, explanationRevisionUid, explanationReservationUid } = props

  const { initializePatientVideoCall } = useRoomMediaContext()

  const toast = useMirohaToast()

  const [roomUid, setRoomUid] = useState<string>()

  const toastError = (error: Error | GqlError) => {
    toast({
      status: 'error',
      title: error.message,
    })
  }

  const navigate = useNavigate()

  const moveToRoom = useCallback(
    (sessionUid: string) => {
      navigate(
        generatePath(Paths.PatientExplanationRoomTop, { trialUid, sessionUid }),
      )
    },
    [navigate, trialUid],
  )

  const [isWaitingSession, setIsWaitingSession] = useState(false)

  const { preSession } = usePreSession({
    isPatient: true,
    explanationRevisionUid,
    explanationReservationUid,
    onSetup: preSession => {
      if (preSession.explanationRevision.latestHistory.status === 'Disabled') {
        navigate(generatePath(Paths.ExplanationRoomNotFound))
        return
      }

      if (!!preSession.videoCallRoomUid) {
        setRoomUid(preSession.videoCallRoomUid)
      }

      if (!preSession.explanationSessionUid) {
        setIsWaitingSession(true)
      }
    },
    onAdditionalFetch: preSession => {
      // セッションが開始したら自動でトップ画面へ（最初からセッションが開始している場合は手動で遷移）
      if (!!preSession.explanationSessionUid && isWaitingSession) {
        moveToRoom(preSession.explanationSessionUid)
      }
    },
    onError: error => {
      toastError(error)
    },
  })

  const candidateId =
    preSession?.explanationRevision.explanation.patient.candidateId

  useEffect(() => {
    if (!roomUid || !candidateId) return
    initializePatientVideoCall({
      roomUid,
      candidateId,
    })
  }, [roomUid, candidateId, initializePatientVideoCall])

  return {
    preSession,
    moveToRoom,
  }
}
